import { cn, focusHaloFlush, HapticSurface, panelMotion, press } from "@pax/ui";
import { Link, useCanGoBack, useRouter } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";

/**
 * Back to wherever this screen was opened from.
 *
 * Screens nest: opening a scenario from another's similar rail deals a card
 * onto it, and starting a game deals one onto that. The chevron takes the top
 * card off, which is the browser's back button in a different place — session
 * history already holds the stack, so nothing here keeps one. On a cold deep
 * link there is nothing behind, and the store is the only sensible place to go.
 * Screens with their own hierarchy pass `onBack` to go up a level instead.
 */
export function BackChevron({ className, onBack }: { className?: string; onBack?: () => void }) {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const chrome = cn(
    "relative flex size-9 items-center justify-center rounded-full bg-background/70 text-foreground shadow-sm ring-1 ring-foreground/10 sm:size-10",
    panelMotion,
    press,
    focusHaloFlush,
    className,
  );

  if (!onBack && !canGoBack) {
    return (
      <Link to="/" aria-label="All scenarios" className={chrome}>
        <ChevronLeft className="size-4 sm:size-5" />
        <HapticSurface />
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onBack ?? (() => router.history.back())}
      aria-label="Back"
      className={chrome}
    >
      <ChevronLeft className="size-4 sm:size-5" />
      <HapticSurface />
    </button>
  );
}
